import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Grace period (ms) before a submenu actually closes when the cursor
   * leaves its title. This lets the cursor briefly pass over a sibling
   * title (e.g. while moving diagonally down into the open submenu)
   * without instantly swapping to that sibling's content.
   */
  #SWITCH_GRACE_PERIOD_MS = 150;

  /**
   * @type {number | null}
   */
  #deactivateTimeout = null;

  /**
   * @type {HTMLElement | null}
   */
  #pendingActivationItem = null;

  /**
   * @type {PointerEvent | FocusEvent | null}
   */
  #pendingActivationEvent = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
    this.#cancelPendingSwitch();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Handle pointer/focus entering a menu item. If the previously active
   * item hasn't actually closed yet (its deactivation is still in its
   * grace period), the switch is deferred until that grace period
   * resolves, so a quick diagonal pass over this item doesn't interrupt
   * the user heading toward the open submenu.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    if (item === this.#state.activeItem) {
      // Cursor returned to (or never really left) the active item —
      // cancel any pending close/switch that was queued for it.
      this.#cancelPendingSwitch();
      return;
    }

    const isHoverIntent = event instanceof PointerEvent;

    if (isHoverIntent && this.#deactivateTimeout) {
      // The active item's submenu hasn't closed yet. Hold off switching
      // in case the cursor is only passing over this item on its way into
      // that open submenu — only commit once the grace period elapses.
      this.#pendingActivationItem = item;
      this.#pendingActivationEvent = event;
      return;
    }

    this.#cancelPendingSwitch();
    this.#commitActivation(item, event);
  };

  /**
   * Actually activates a menu item: shows its submenu and updates state.
   * @param {HTMLElement} item
   * @param {PointerEvent | FocusEvent} event
   */
  #commitActivation = (item, event) => {
    if (!(event.target instanceof Element)) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Handle pointer/focus leaving a menu item. Instead of closing the
   * submenu instantly, a short grace period is started — if the cursor
   * comes back to this item (or settles on a different one) within that
   * window, the appropriate action is taken then instead of immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    if (event instanceof FocusEvent) {
      // Keyboard navigation shouldn't have hover-intent delays.
      this.#deactivate();
      return;
    }

    this.#scheduleDeactivation(this.#state.activeItem);
  }

  /**
   * Queues the actual close of an item's submenu after the grace period.
   * If, by the time the timer fires, a different item was waiting to take
   * over (because the cursor settled there instead), that switch is
   * committed right after closing this one.
   * @param {HTMLElement | null} item
   */
  #scheduleDeactivation(item) {
    this.#cancelPendingSwitch();

    this.#deactivateTimeout = window.setTimeout(() => {
      this.#deactivateTimeout = null;

      if (item && item === this.#state.activeItem) {
        this.#deactivate(item);
      }

      if (this.#pendingActivationItem) {
        const nextItem = this.#pendingActivationItem;
        const nextEvent = this.#pendingActivationEvent;
        this.#pendingActivationItem = null;
        this.#pendingActivationEvent = null;

        if (nextItem !== this.#state.activeItem && nextEvent) {
          this.#commitActivation(nextItem, nextEvent);
        }
      }
    }, this.#SWITCH_GRACE_PERIOD_MS);
  }

  /**
   * Cancels any pending deactivation/switch and clears related state.
   */
  #cancelPendingSwitch() {
    if (this.#deactivateTimeout) {
      window.clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
    this.#pendingActivationItem = null;
    this.#pendingActivationEvent = null;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}